import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

/**
 * Component to handle token-based auto-login from mobile app
 * 
 * Uses Token Exchange (Option B):
 * 1. Mobile app appends its JWT token to the URL
 * 2. This component extracts the token and calls /api/auth/mobile-handoff
 * 3. Backend validates mobile token and returns NEW web tokens
 * 4. Web tokens are stored and used for the web session
 * 
 * Benefits:
 * - Mobile and web sessions are completely independent
 * - Logging out on web does NOT affect mobile session
 * - Web tokens follow normal web token lifecycle (2hr access, 7day refresh)
 * - Token refresh works normally on web
 */
const MobileTokenAuthHandler = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const processingRef = useRef(false);

  // Handle auth errors by redirecting to login with error message
  const handleAuthError = useCallback((errorType) => {
    console.error('[MobileAuth] Auth error:', errorType);
    processingRef.current = false;
    
    // Determine login path based on current URL
    let loginPath = '/admin/login';
    if (location.pathname.includes('/team/')) {
      loginPath = '/team/login';
    } else if (location.pathname.includes('/super-admin/')) {
      loginPath = '/super-admin/login';
    }
    
    // Clean URL and redirect to login with error (using clean URLs)
    const newUrl = `${loginPath}?error=${errorType}`;
    window.location.replace(newUrl);
  }, [location.pathname]);

  // Handle token exchange with mobile token
  const handleTokenExchange = useCallback(async (mobileToken) => {
    try {
      // Decode the token (it may be URL encoded)
      const decodedToken = decodeURIComponent(mobileToken);
      
      // Basic validation before calling API
      const tokenParts = decodedToken.split('.');
      if (tokenParts.length !== 3) {
        console.error('[MobileAuth] Invalid token format - not a JWT');
        handleAuthError('invalid_token');
        return;
      }

      // Call the token exchange endpoint
      console.log('[MobileAuth] Calling token exchange endpoint...');
      const response = await axios.post(`${API}/auth/mobile-handoff`, {
        mobile_token: decodedToken
      });

      console.log('[MobileAuth] Token exchange response:', response.data);

      if (response.data.success) {
        console.log('[MobileAuth] Token exchange successful');
        
        // Determine storage key based on user type
        const userType = response.data.user_type;
        let storageKey = 'token';
        
        if (userType === 'team') {
          storageKey = 'team_token';
          localStorage.setItem('userType', 'team');
        } else if (userType === 'admin') {
          storageKey = 'token';
          localStorage.setItem('userType', 'admin');
        }

        // Store the NEW web tokens (not the mobile token)
        localStorage.setItem(storageKey, response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refreshToken', response.data.refresh_token);
        }
        if (response.data.expires_in) {
          const expiresAt = Date.now() + (response.data.expires_in * 1000);
          localStorage.setItem('tokenExpiresAt', expiresAt.toString());
        }

        // Store user info if provided
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        // Clean the URL by removing the token parameter
        // Use window.location to handle HashRouter properly
        const currentPath = location.pathname;
        console.log('[MobileAuth] Redirecting to clean URL:', currentPath);
        
        // Replace the URL to remove token from history
        const cleanUrl = window.location.origin + window.location.pathname + '#' + currentPath;
        window.history.replaceState(null, '', cleanUrl);
        
        // Force a page reload to ensure the app picks up the new auth state
        // This is necessary because we're using localStorage directly
        window.location.reload();
      } else {
        console.error('[MobileAuth] Token exchange failed:', response.data);
        handleAuthError('exchange_failed');
      }
    } catch (error) {
      console.error('[MobileAuth] Token exchange error:', error);
      
      if (error.response?.status === 401) {
        handleAuthError('token_expired');
      } else if (error.response?.status === 400) {
        handleAuthError('invalid_token');
      } else {
        handleAuthError('network_error');
      }
    }
  }, [location.pathname, handleAuthError]);

  useEffect(() => {
    // Prevent duplicate processing
    if (processingRef.current) {
      return;
    }

    // Debug logging
    console.log('[MobileAuth] Checking for token...', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      fullUrl: window.location.href
    });

    // For BrowserRouter, query params are in location.search
    // Also check hash for backward compatibility with old URLs
    let tokenValue = null;
    
    // Method 1: Check react-router's location.search (BrowserRouter)
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      tokenValue = searchParams.get('token');
      if (tokenValue) {
        console.log('[MobileAuth] Token found in location.search');
      }
    }
    
    // Method 2: Check window.location.hash for backward compatibility
    // This handles old URLs: /#/admin/plans?token=xxx
    if (!tokenValue && window.location.hash.includes('?')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashQuery = hashParts[1];
        const hashParams = new URLSearchParams(hashQuery);
        tokenValue = hashParams.get('token');
        if (tokenValue) {
          console.log('[MobileAuth] Token found in window.location.hash (backward compat)');
        }
      }
    }

    // Method 3: Check the full URL as fallback
    if (!tokenValue) {
      const fullUrl = window.location.href;
      const tokenMatch = fullUrl.match(/[?&]token=([^&]+)/);
      if (tokenMatch) {
        tokenValue = tokenMatch[1];
        console.log('[MobileAuth] Token found via URL regex match');
      }
    }

    if (tokenValue) {
      console.log('[MobileAuth] Token detected, initiating token exchange...');
      processingRef.current = true;
      handleTokenExchange(tokenValue);
    } else {
      console.log('[MobileAuth] No token found in URL');
    }
  }, [location.pathname, location.search, handleTokenExchange]);

  return children;
};

export default MobileTokenAuthHandler;
